import Button from "@/components/Button";
import MultiSelectDropdown from "@/components/dropdowns/MultiSelectDropdown/MultiSelectDropdown";
import { positionsInSections } from "@/components/features/SquadBuilder/RightPanel/PlayerRoleMode/PlayerRoleMode";
import { favorite } from "@/designSystemIcons";
import {
  contentLabelsSelector,
  formationsAsListSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import { AppDispatch } from "@/redux/store";
import {
  Author,
  FilterFormat,
  clearFiltersInCategory,
  tacticsSelector,
  toggleFavoriteFilter,
  toggleFilter,
} from "@/redux/tactics/tacticsSlice";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import Accordion from "../../../containers/Accordion";
import TacticActiveFiltersRow from "../TacticActiveFiltersRow";
import TacticImage from "../TacticImage";

export const FORMATION_GROUPS_3_AT_THE_BACK = "3 at the back";
export const FORMATION_GROUPS_4_AT_THE_BACK = "4 at the back";
export const FORMATION_GROUPS_5_AT_THE_BACK = "5 at the back";

interface MobileQuickFiltersProps {
  isFavoritesOn: boolean;
  handleToggleFavorites: () => void;
}

const MobileQuickFilters = ({
  isFavoritesOn,
  handleToggleFavorites,
}: MobileQuickFiltersProps) => {
  const { t } = useTranslation(["tactics", "common"]);
  return (
    <section className="flex flex-col gap-y-4 px-4 h-full mt-2">
      <div className="flex flex-col gap-y-1 h-full">
        <label>{t("filters.quickFilters")}</label>
        <Button
          variant={"tertiary"}
          label={t("common:favorites")}
          onClick={handleToggleFavorites}
          active={isFavoritesOn}
          icon={<div className="w-5 h-5">{favorite}</div>}
        />
      </div>
    </section>
  );
};

interface FormationsSectionProps {
  selectedFormations: FilterFormat[];
  selectedFormationGroups: FilterFormat[];
  handleClickedFormation: (value: FilterFormat) => void;
  handleClickedFormationGroup: (value: FilterFormat) => void;
  handleClearFormations: () => void;
}

const FormationsSection = ({
  selectedFormations,
  selectedFormationGroups,
  handleClickedFormation,
  handleClickedFormationGroup,
  handleClearFormations,
}: FormationsSectionProps) => {
  const formations = useSelector(formationsAsListSelector);
  const { t } = useTranslation("tactics");
  const formationsGroups = useMemo(
    () =>
      [
        {
          label: t("filters.threeAtTheBack"),
          value: FORMATION_GROUPS_3_AT_THE_BACK,
        },
        {
          label: t("filters.fourAtTheBack"),
          value: FORMATION_GROUPS_4_AT_THE_BACK,
        },
        {
          label: t("filters.fiveAtTheBack"),
          value: FORMATION_GROUPS_5_AT_THE_BACK,
        },
      ] as FilterFormat[],
    [formations, t],
  );

  return (
    <Accordion label={t("filters.formations")} defaultOpen={true}>
      <section className="flex flex-col gap-y-4 px-4 h-full">
        <div className="flex flex-col gap-y-1 h-full">
          <label>{t("filters.specificFormations")}</label>
          <MultiSelectDropdown
            defaultOption={t("filters.formations")}
            options={formations.map((formation) => ({
              label: formation.label,
              value: formation.id,
            }))}
            variant="tertiary"
            selectedOptions={selectedFormations}
            onChange={(option) => handleClickedFormation(option)}
            handleClearAll={handleClearFormations}
            align="left"
          />
        </div>
        <div className="flex flex-col gap-y-1 h-full">
          <label>{t("filters.formationGroups")}</label>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="tertiary"
              label={formationsGroups[0].label}
              active={selectedFormationGroups.some(
                (formationGroup) =>
                  formationGroup.value === formationsGroups[0].value,
              )}
              onClick={() =>
                handleClickedFormationGroup({
                  label: formationsGroups[0].label,
                  value: formationsGroups[0].value,
                })
              }
            />
            <Button
              variant="tertiary"
              label={formationsGroups[1].label}
              active={selectedFormationGroups.some(
                (formationGroup) =>
                  formationGroup.value === formationsGroups[1].value,
              )}
              onClick={() =>
                handleClickedFormationGroup({
                  label: formationsGroups[1].label,
                  value: formationsGroups[1].value,
                })
              }
            />
            <Button
              variant="tertiary"
              label={formationsGroups[2].label}
              active={selectedFormationGroups.some(
                (formationGroup) =>
                  formationGroup.value === formationsGroups[2].value,
              )}
              onClick={() =>
                handleClickedFormationGroup({
                  label: formationsGroups[2].label,
                  value: formationsGroups[2].value,
                })
              }
            />
          </div>
        </div>
      </section>
    </Accordion>
  );
};

interface PositionsSectionProps {
  selectedPositions: string[];
  handleClickedPosition: (position: string) => void;
}
const PositionsSection = ({
  selectedPositions,
  handleClickedPosition,
}: PositionsSectionProps) => {
  const { attackers, midfielders, defenders } = positionsInSections;
  const { positions } = useSelector(contentLabelsSelector);
  const { t } = useTranslation("tactics");
  const zoneName = (ids: string[], fallback: string) =>
    positions[ids[0]]?.zoneName ?? fallback;

  return (
    <Accordion label={t("filters.positions")} defaultOpen={true}>
      <section className="flex flex-col gap-y-4 px-4">
        <div className="flex flex-col">
          <p className="text-xs">
            {zoneName(attackers, t("filters.attackers"))}
          </p>
          <div className="flex flex-wrap gap-2">
            {attackers.map((attacker) => (
              <Button
                variant="tertiary"
                key={attacker}
                active={selectedPositions.includes(attacker)}
                onClick={() => handleClickedPosition(attacker)}
                label={positions[attacker]?.name ?? attacker}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col">
          <p className="text-xs">
            {zoneName(midfielders, t("filters.midfielders"))}
          </p>
          <div className="flex flex-wrap gap-2">
            {midfielders.map((midfielder) => (
              <Button
                variant="tertiary"
                key={midfielder}
                active={selectedPositions.includes(midfielder)}
                onClick={() => handleClickedPosition(midfielder)}
                label={positions[midfielder]?.name ?? midfielder}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col">
          <p className="text-xs">
            {zoneName(defenders, t("filters.defenders"))}
          </p>
          <div className="flex flex-wrap gap-2">
            {defenders.map((defender) => (
              <Button
                variant="tertiary"
                key={defender}
                active={selectedPositions.includes(defender)}
                onClick={() => handleClickedPosition(defender)}
                label={positions[defender]?.name ?? defender}
              />
            ))}
          </div>
        </div>
      </section>
    </Accordion>
  );
};

interface AuthorsSectionProps {
  authors: Author[];
  selectedAuthors: string[];
  handleClickedAuthor: (author: Author) => void;
}
const AuthorsSection = ({
  authors,
  selectedAuthors,
  handleClickedAuthor,
}: AuthorsSectionProps) => {
  const { t } = useTranslation("tactics");
  return (
    <Accordion label={t("filters.authors")} defaultOpen={true}>
      <section className="grid grid-cols-4 desktop:grid-cols-3 gap-1">
        {authors.map((author) => (
          <TacticImage
            key={author.name}
            imageUrl={author.logoUrl}
            author={author.name}
            isTrending={false}
            noBorder={true}
            selectable={true}
            selected={selectedAuthors.includes(author.name)}
            onClick={() => handleClickedAuthor(author)}
          />
        ))}
      </section>
    </Accordion>
  );
};

const TacticFiltersSidebar = () => {
  const { authors, selectedFilters, isFavoriteFilterOn } =
    useSelector(tacticsSelector);
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  return (
    <section className="flex flex-col w-full gap-y-4 desktop:text-xs">
      {isMobile && (
        <>
          <TacticActiveFiltersRow isInsideFiltersSidebar={true} />
          <MobileQuickFilters
            isFavoritesOn={isFavoriteFilterOn}
            handleToggleFavorites={() => {
              dispatch(toggleFavoriteFilter(!isFavoriteFilterOn));
            }}
          />
        </>
      )}

      <FormationsSection
        selectedFormations={selectedFilters.formation.map((formation) => ({
          label: formation.label,
          value: formation.value,
        }))}
        selectedFormationGroups={selectedFilters.formationGroup.map(
          (formationGroup) => ({
            label: formationGroup.label,
            value: formationGroup.value,
          }),
        )}
        handleClickedFormation={(formation) => {
          dispatch(
            toggleFilter({
              category: "formation",
              value: formation.value,
              label: formation.label,
            }),
          );
        }}
        handleClickedFormationGroup={(formationGroup) => {
          dispatch(
            toggleFilter({
              category: "formationGroup",
              value: formationGroup.value,
              label: formationGroup.label,
            }),
          );
        }}
        handleClearFormations={() => {
          dispatch(clearFiltersInCategory({ category: "formation" }));
        }}
      />
      <PositionsSection
        selectedPositions={selectedFilters.positions.map(
          (position) => position.value,
        )}
        handleClickedPosition={(position) => {
          dispatch(
            toggleFilter({
              category: "positions",
              value: position,
              label: position,
            }),
          );
        }}
      />
      <AuthorsSection
        authors={authors}
        selectedAuthors={selectedFilters.author.map((author) => author.value)}
        handleClickedAuthor={(author) => {
          dispatch(
            toggleFilter({
              category: "author",
              value: author.name,
              label: author.name,
            }),
          );
        }}
      />
    </section>
  );
};

export default TacticFiltersSidebar;
